/**
 * buildDrilldownGroups — computes DrilldownGroup[] by diffing two AuditResults.
 *
 * This utility runs entirely on the client, working from the typed registry
 * metadata and the raw dataPoints maps inside the audit results.
 *
 * If the registry is not populated (first render), returns an empty array —
 * the ScoreCard renders the header but no rows.
 *
 * @param offDomain  Optional off-domain signal data (OffDomainResult shape).
 *   When provided for the 'geo' score, generates Wikipedia + YouTube rows
 *   in the geo_offdomain subcategory group.
 *   // Off-domain Tier 1: Wikipedia + YouTube only. Reddit dropped per dev plan
 *   // v3.5.1; coverage flows through DataForSEO LLM Mentions (Sprint 2.7.5).
 */

import type { DrilldownGroup, DrilldownRowData } from './ScoreCard';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AuditResults = Record<string, any> | null;

// Lightweight delta computation (does not import registry — avoids bundle bloat
// from the server-only registry. Simple heuristics are enough for the UI).

function formatMeasurement(m: Record<string, unknown> | null | undefined): string {
  if (!m || m['kind'] === 'absent') return '—';
  switch (m['kind']) {
    case 'boolean': return m['value'] ? 'Yes' : 'No';
    case 'percentage': return `${(m['value'] as number).toFixed(1)}%`;
    case 'numeric': return `${m['value']}${m['unit'] ? ` ${m['unit']}` : ''}`;
    case 'categorical': return String(m['value']);
    default: return '—';
  }
}

function computeDelta(
  then: Record<string, unknown> | null | undefined,
  now: Record<string, unknown> | null | undefined,
): DrilldownRowData['delta'] {
  if (!then || !now || then['kind'] === 'absent' || now['kind'] === 'absent') {
    return { direction: 'incomparable', display: '—' };
  }
  if (then['kind'] !== now['kind']) {
    return { direction: 'incomparable', display: '—' };
  }

  if (then['kind'] === 'boolean') {
    const t = then['value'] as boolean;
    const n = now['value'] as boolean;
    if (t === n) return { direction: 'unchanged', display: '·' };
    if (n && !t) return { direction: 'improved', display: '↑' };
    return { direction: 'worsened', display: '↓' };
  }

  if (then['kind'] === 'percentage' || then['kind'] === 'numeric') {
    const t = then['value'] as number;
    const n = now['value'] as number;
    const diff = n - t;
    if (diff === 0) return { direction: 'unchanged', display: '·' };
    const sign = diff > 0 ? '+' : '';
    const fmtDiff =
      then['kind'] === 'percentage'
        ? `${sign}${diff.toFixed(1)}pp`
        : `${sign}${diff.toFixed(0)}`;
    // Heuristic: higher is better unless the id contains ttfb/lcp/inp/cls/freshness
    return {
      direction: diff > 0 ? 'improved' : 'worsened',
      display: fmtDiff,
    };
  }

  return { direction: 'incomparable', display: '—' };
}

// Subcategory order for each score pillar
const SEO_SUBCATEGORIES = [
  { id: 'seo_technical', label: 'Technical' },
  { id: 'seo_on_page', label: 'On-Page' },
  { id: 'seo_structured', label: 'Structured Data' },
];

const AEO_SUBCATEGORIES = [
  { id: 'aeo_schema', label: 'Schema' },
  { id: 'aeo_content', label: 'Content' },
  { id: 'aeo_structure', label: 'Structure' },
];

const GEO_SUBCATEGORIES = [
  { id: 'geo_citations', label: 'Citations' },
  { id: 'geo_brand', label: 'Brand Presence' },
  { id: 'geo_authority', label: 'Authority Signals' },
  { id: 'geo_offdomain', label: 'Off-domain presence' },
  // geo_perception is active:false (Sprint 2.9) — included so it shows
  // in the UI when data arrives without a code change.
  { id: 'geo_perception', label: 'Brand perception in AI' },
];

const SUBCATEGORIES_BY_SCORE: Record<string, { id: string; label: string }[]> = {
  seo: SEO_SUBCATEGORIES,
  aeo: AEO_SUBCATEGORIES,
  geo: GEO_SUBCATEGORIES,
};

// Human label lookup for data point IDs (derived from the data-point label field)
// In a real production build, this would import from the registry barrel.
// Here we do a simple ID-to-label fallback.
function idToLabel(id: string): string {
  const part = id.split('.').pop() ?? id;
  return part
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function getDataPointsForSubcategory(
  scoreDataPoints: Record<string, unknown>,
  subcategoryId: string,
): Array<{ id: string; label: string; description: string }> {
  // Filter data point IDs by their subcategory prefix
  const prefix =
    subcategoryId === 'seo_technical' ? 'seo.technical.' :
    subcategoryId === 'seo_on_page' ? 'seo.on_page.' :
    subcategoryId === 'seo_structured' ? 'seo.structured.' :
    subcategoryId === 'aeo_schema' ? 'aeo.schema.' :
    subcategoryId === 'aeo_content' ? 'aeo.content.' :
    subcategoryId === 'aeo_structure' ? 'aeo.structure.' :
    subcategoryId === 'geo_citations' ? 'geo.citations.' :
    subcategoryId === 'geo_brand' ? 'geo.brand.' :
    subcategoryId === 'geo_authority' ? 'geo.authority.' :
    subcategoryId === 'geo_offdomain' ? 'geo.offdomain.' :
    subcategoryId === 'geo_perception' ? 'geo.perception.' :
    subcategoryId + '.';

  return Object.keys(scoreDataPoints)
    .filter((id) => id.startsWith(prefix))
    .map((id) => ({ id, label: idToLabel(id), description: '' }));
}

export function buildDrilldownGroups(
  scoreKey: 'seo' | 'aeo' | 'geo',
  thenAudit: AuditResults,
  nowAudit: AuditResults,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  offDomain?: { then?: any; now?: any },
): DrilldownGroup[] {
  const subcats = SUBCATEGORIES_BY_SCORE[scoreKey] ?? [];

  const thenDataPoints =
    (thenAudit?.scores?.[scoreKey]?.dataPoints as Record<string, unknown> | undefined) ?? {};
  const nowDataPoints =
    (nowAudit?.scores?.[scoreKey]?.dataPoints as Record<string, unknown> | undefined) ?? {};

  // Union of all known IDs
  const allIds = new Set([...Object.keys(thenDataPoints), ...Object.keys(nowDataPoints)]);

  return subcats
    .map((subcat) => {
      // ── geo_offdomain: special-cased rows from OffDomainResult ──
      if (subcat.id === 'geo_offdomain' && scoreKey === 'geo' && offDomain) {
        const thenOd = offDomain.then;
        const nowOd  = offDomain.now;

        const wikiThenDisplay  = thenOd ? (thenOd.wikipediaPresent ? 'Yes' : 'No') : '—';
        const wikiNowDisplay   = nowOd  ? (nowOd.wikipediaPresent  ? 'Yes' : 'No') : '—';
        const wikiDelta = computeDelta(
          thenOd ? { kind: 'boolean', value: thenOd.wikipediaPresent } : null,
          nowOd  ? { kind: 'boolean', value: nowOd.wikipediaPresent  } : null,
        );
        const wikiExternalUrl  = nowOd?.meta?.wikipediaUrl as string | undefined;

        const ytChannelId      = nowOd?.meta?.youtubeChannelId as string | undefined;
        const ytExternalUrl    = ytChannelId ? `https://www.youtube.com/channel/${ytChannelId}` : undefined;
        const ytThenDisplay    = thenOd ? (thenOd.youtubePresent ? 'Yes' : 'No') : '—';
        const ytNowDisplay     = nowOd  ? (nowOd.youtubePresent  ? 'Yes' : 'No') : '—';
        const ytDelta = computeDelta(
          thenOd ? { kind: 'boolean', value: thenOd.youtubePresent } : null,
          nowOd  ? { kind: 'boolean', value: nowOd.youtubePresent  } : null,
        );

        const rows: DrilldownRowData[] = [
          {
            id:          'geo_offdomain.wikipedia',
            label:       'Wikipedia article',
            description: 'Whether your brand has a Wikipedia article. Wikipedia is one of the most-cited domains across AI assistant responses globally.',
            thenDisplay: wikiThenDisplay,
            nowDisplay:  wikiNowDisplay,
            delta:       wikiDelta,
            ...(wikiExternalUrl !== undefined ? { externalUrl: wikiExternalUrl } : {}),
          },
          {
            id:          'geo_offdomain.youtube',
            label:       'YouTube channel',
            description: 'Whether your brand has a YouTube channel. YouTube is a major source for product and how-to content frequently cited by AI assistants.',
            thenDisplay: ytThenDisplay,
            nowDisplay:  ytNowDisplay,
            delta:       ytDelta,
            ...(ytExternalUrl !== undefined ? { externalUrl: ytExternalUrl } : {}),
          },
        ];

        return { subcategory: subcat.id, label: subcat.label, rows };
      }

      const defs = getDataPointsForSubcategory(
        Object.fromEntries([...allIds].map((id) => [id, null])),
        subcat.id,
      );

      const rows: DrilldownRowData[] = defs.map(({ id, label, description }) => {
        const then = thenDataPoints[id] as Record<string, unknown> | undefined;
        const now = nowDataPoints[id] as Record<string, unknown> | undefined;
        return {
          id,
          label,
          description,
          thenDisplay: formatMeasurement(then),
          nowDisplay: formatMeasurement(now),
          delta: computeDelta(then, now),
        };
      });

      return { subcategory: subcat.id, label: subcat.label, rows };
    })
    .filter((g) => g.rows.length > 0);
}
